import { useCallback } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import VendNumber, { vn } from 'vend-number'
import trim from 'lodash/trim'
import { trackError } from '@vend/utilities'
import {
  applyLspCashDiscountToOutstanding,
  roundCash,
  shouldApplyCashDiscountingServiceFee,
} from '../../../../functions/payments'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleFinals,
  selectLspCashDiscountPercentage,
  selectSaleTotals,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { isCorrectlyRounded } from '../../../helpers/isCorrectlyRounded'
import {
  CashDiscountingPaymentOption,
  CashPaymentOption,
  IntegratedPaymentOption,
  LspayCashDiscountingPaymentOption,
  SalePayment,
} from '../PaymentModal/types'
import {
  RoundedNumber,
  CashPaymentInformation,
} from '../../../payments/cash-payment-modal/CashPaymentModal'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'
import { isLspCashDiscountBlocked } from '../isPartialAmountTendered'
import { useHandleIntegrationPayment } from './useHandleIntegrationPayment'
import { useFetchPaymentTypes } from './useFetchPaymentTypes'
import { useLspCashDiscountBlockedSale } from './useLspCashDiscountBlockedSale'

/** Normalise a `roundCash()` result into the {@link RoundedNumber} shape used by payment options. */
const toRoundedNumber = (
  value: ReturnType<typeof roundCash>
): RoundedNumber => ({
  rounded: vn(value.rounded.toString()),
  remainder: value.remainder,
})

/**
 * Build an {@link LspayCashDiscountingPaymentOption} for the cash modal.
 * The discounted `roundedBalance` is derived via
 * {@link applyLspCashDiscountToOutstanding} using the sale exclusive price;
 * the Pay-field amount is passed through unchanged.
 */
const buildLspayCashDiscountingOption = (
  paymentType: ApiRetailerPaymentType,
  rounding: string,
  balance: VendNumber | string | number,
  roundedAmountTendered: ReturnType<typeof roundCash>,
  roundedBalance: ReturnType<typeof roundCash>,
  exclusivePrice: number | string,
  tax: number | string,
  rate: number
): LspayCashDiscountingPaymentOption => ({
  paymentModalType: 'lspayCashDiscounting',
  paymentType,
  rounding,
  lspCashDiscountPercentage: rate,
  // Only the outstanding balance is discounted; the Pay-field amount is kept
  // as-is so the modal can record the full cash the customer actually gives.
  roundedAmountTendered: toRoundedNumber(roundedAmountTendered),
  roundedBalance: toRoundedNumber(
    roundCash(
      applyLspCashDiscountToOutstanding(
        vn(balance).toString(),
        exclusivePrice,
        rate,
        tax
      ).toFixed(2),
      paymentType
    )
  ),
  originalRoundedAmountTendered: toRoundedNumber(roundedAmountTendered),
  originalRoundedBalance: toRoundedNumber(roundedBalance),
  exclusivePrice,
  tax,
})

/**
 * Handle complete cash payment.
 *
 * @param onPaymentSuccess
 * @param roundingPaymentType Optional rounding payment type. Used when the
 *        cash payment leaves a remainder; the rounding type covers that
 *        remainder before the cash payment is saved via `onPaymentSuccess`.
 */
export const useCompleteCashPayment = (
  onPaymentSuccess: (result: SalePayment) => void,
  roundingPaymentType?: ApiRetailerPaymentType
) => {
  const saleManager = useSaleManager()

  const tenderRoundedCash = useCallback(
    (
      cashAmount: string,
      paymentType: ApiRetailerPaymentType,
      changeAmount?: string
    ) => {
      return onPaymentSuccess({
        paymentType,
        paymentAmount: cashAmount,
        changeAmount,
      })
    },
    [onPaymentSuccess]
  )

  const completeRoundCashPayment = useCallback(
    async (
      { remainder, cashAmount, changeAmount }: CashPaymentInformation,
      paymentType: ApiRetailerPaymentType
    ) => {
      if (vn(0).eq(remainder)) {
        await tenderRoundedCash(cashAmount, paymentType, changeAmount)
        return
      }

      if (roundingPaymentType) {
        await saleManager.pay(remainder, roundingPaymentType).catch(err => {
          trackError(err)
        })
      }

      await tenderRoundedCash(cashAmount, paymentType, changeAmount)
    },
    [roundingPaymentType, saleManager, tenderRoundedCash]
  )

  return {
    completeRoundCashPayment,
  }
}

/**
 * Handle cash payment
 * @returns function to handle cash payment, it should do all the necessary things to build cash payment option
 */
export const useHandleCashPayment = (
  amount: string,
  onPaymentSuccess: (result: SalePayment) => void
) => {
  const balance = useAppSelector(selectOutstandingBalance)
  const saleFinals = useAppSelector(selectSaleFinals)
  const lspCashDiscountPercentage = useAppSelector(
    selectLspCashDiscountPercentage
  )
  const totals = useAppSelector(selectSaleTotals)
  const lspCashDiscountBlockedSale = useLspCashDiscountBlockedSale()
  // The LSPay rate is fetched on Sale component mount via `useApplyLspCashDiscount`
  // and stored on the current sale. Reading from Redux here keeps the cash-button
  // click path synchronous (matching the pre-LSPay behaviour).
  const { handleIntegrationPayment } = useHandleIntegrationPayment(amount)
  const registerManager = useRegisterManager()
  const saleManager = useSaleManager()
  const hasCashDiscounting = registerManager.hasCashDiscounting()

  const { systemPaymentTypes } = useFetchPaymentTypes()
  const { completeRoundCashPayment } = useCompleteCashPayment(
    onPaymentSuccess,
    systemPaymentTypes.roundingPaymentType
  )

  const handleCashDiscountingPayment = useCallback(
    (paymentType: ApiRetailerPaymentType): CashDiscountingPaymentOption => ({
      paymentModalType: 'cashDiscounting',
      paymentType,
      amountTendered: amount,
    }),
    [amount]
  )

  const handleCashPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ):
      | CashPaymentOption
      | CashDiscountingPaymentOption
      | LspayCashDiscountingPaymentOption
      | IntegratedPaymentOption
      | Promise<IntegratedPaymentOption>
      | undefined => {
      const amountTendered = vn(amount)
      const rounding = paymentType.config?.rounding || '0.01'
      const roundedAmountTendered = roundCash(amountTendered, paymentType)
      const roundedBalance = roundCash(balance, paymentType)

      if (paymentType.config && trim(paymentType.config.url)) {
        // Some people have cash, but with a URL for some reason. It behaves like an integrated payment for them
        return handleIntegrationPayment(paymentType)
      }

      // LSPay cash discounting takes priority over register cash discounting
      // when both are configured with a rate > 0. Route to the LSPay modal when
      // the rate populated by `useApplyLspCashDiscount` is greater than 0. We
      // only apply this for positive-balance, full-payment flows — partial
      // tenders and refunds fall through to the normal cash path.
      if (
        lspCashDiscountPercentage > 0 &&
        vn(balance).gt(0) &&
        amountTendered.gt(0) &&
        !isLspCashDiscountBlocked(
          amount,
          lspCashDiscountPercentage,
          lspCashDiscountBlockedSale
        )
      ) {
        // Notify the CFD that cash payment with a discount has been selected.
        // sale-change-listener picks this up to publish a SALE_UPDATED event
        // that includes cashDiscount data and an adjusted total.
        window.dispatchEvent(new CustomEvent('cfd-cash-payment-selected'))
        // Over-tendering skips the modal and completes immediately, matching
        // the plain-cash path when the Pay input exceeds the balance.
        if (
          isCorrectlyRounded(amountTendered, vn(rounding)) &&
          amountTendered.gt(balance)
        ) {
          saleManager
            .setLspCashDiscountPercentage(lspCashDiscountPercentage)
            .catch(() => undefined)

          const discountedBalance = roundCash(
            applyLspCashDiscountToOutstanding(
              balance,
              saleFinals.exclusivePrice,
              lspCashDiscountPercentage,
              saleFinals.tax
            ).toFixed(2),
            paymentType
          )
          const discountedOwed = vn(discountedBalance.rounded)
          const roundedTendered = vn(roundedAmountTendered.rounded)

          completeRoundCashPayment(
            {
              remainder: discountedBalance.remainder.toString(),
              // Record the full cash tender; the LSP discount reduces the sale
              // total only, not the amount the customer physically handed over.
              cashAmount: roundedAmountTendered.rounded.toString(),
              changeAmount: roundedTendered.gt(discountedOwed)
                ? roundedTendered.minus(discountedOwed).toString()
                : undefined,
            },
            paymentType
          ).catch(() => undefined)
          return undefined
        }

        return buildLspayCashDiscountingOption(
          paymentType,
          rounding,
          balance,
          roundedAmountTendered,
          roundedBalance,
          saleFinals.exclusivePrice,
          saleFinals.tax,
          lspCashDiscountPercentage
        )
      }

      if (
        shouldApplyCashDiscountingServiceFee(hasCashDiscounting, {
          totals,
          lspCashDiscountPercentage,
        })
      ) {
        return handleCashDiscountingPayment(paymentType)
      }

      if (
        // Don't skip if it's not correctly rounded
        isCorrectlyRounded(amountTendered, vn(rounding)) &&
        // Skip if it's correctly rounded and 0 (0 dollar cash payment)
        (amountTendered.isZero() ||
          // Skip if it's correctly rounded, positive and greater than the balance
          (amountTendered.gt(0) && amountTendered.gt(balance)))
        // Don't ever skip for refunds as we always want to show the modal
      ) {
        completeRoundCashPayment(
          {
            remainder: roundedBalance.remainder.toString(),
            cashAmount: roundedAmountTendered.rounded.toString(),
          },
          paymentType
        ).catch(() => undefined)
        return undefined
      }

      return {
        paymentModalType: 'cash',
        paymentType,
        roundedAmountTendered: toRoundedNumber(roundedAmountTendered),
        roundedBalance: toRoundedNumber(roundedBalance),
        rounding,
      }
    },
    [
      amount,
      balance,
      completeRoundCashPayment,
      hasCashDiscounting,
      lspCashDiscountPercentage,
      handleIntegrationPayment,
      handleCashDiscountingPayment,
      saleManager,
      lspCashDiscountBlockedSale,
      saleFinals,
      totals,
    ]
  )

  return {
    handleCashPayment,
  }
}
